<script setup>
    import RichTextEditor from "@/components/Editor/RichTextEditor.vue";
    import { ref, toRefs } from "vue";

    const editorComp = ref(null);

    const props = defineProps({
        /**
         * Innehållet i editorn
         */
        content: {
            type: [Object, String],
        },
        options: {
            type: Array,
            required: false,
        },
        /**
         * Värden som ska kollas mot för att se om en fras som lagts in i content har ett värde. Om det inte har ett
         * värde kommet ett felmeddelande skrivas ut om att frasen saknar värde
         */
        frasValues: {
            type: Map,
            required: false,
        },
        /**
         * Ska content hanteras som icke-rich text?
         */
        simpleEditor: {
            type: Boolean,
            required: false,
            default: () => false,
        },
        /**
         * Ska fraser användas? Avgör om fras-knappen kommer renderas
         */
        withFraser: {
            type: Boolean,
            required: false,
            default: () => true,
        },
        typeLabel: {
            type: String,
            required: false,
        },
        anmalanPatientMap: {
            type: Map,
            required: false,
        },
        /**
         * Ska toolbar visas i headern?
         */
        enableBar: {
            type: Boolean,
            required: false,
            default: () => true,
        },
        padding: {
            type: String,
            required: false,
            default: "default",
            validator: function (value) {
                return ["default", "none"].indexOf(value) !== -1;
            },
        },
        /**
         * Värden som fraser ska konverteras till och sättas i content. convertFrasToValue måste vara true för att
         * konvertering ska ske. Istället för $enfras kommer $enfras översättas till motsvarande värde
         */
        convertValues: {
            type: Map,
            required: false,
        },
        /**
         * Ska fraser konverteras till dess värde vid inmatning? Istället för $enfras kommer $enfras översättas till
         * motsvarande värde
         */
        convertFrasToValue: {
            type: Boolean,
            required: false,
            default: () => false,
        },
    });

    const emit = defineEmits(["update:content"]);

    const { anmalanPatientMap, frasValues, simpleEditor, typeLabel } = toRefs(props);

    const frasErrorsPerObject = ref(new Map());
    const frasErrors = ref([]);

    function checkFraser({ data, values }) {
        const editorContent = JSON.stringify(data);
        const errors = new Map();

        if (values) {
            for (let [id, value] of values) {
                if (value) {
                    const valueErrors = [];

                    value.forEach((value, fras) => {
                        if (!value && editorContent.includes(fras)) {
                            /*
                                TODO Gör det mer generiskt. Editorn används för mer än bara anmälan
                             */
                            if (anmalanPatientMap?.value) {
                                valueErrors.push(
                                    `${typeLabel.value} till ${anmalanPatientMap.value.get(
                                        id
                                    )} kommer inte skickas eftersom det innehåller frasen $${fras} som saknas.`
                                );
                            } else {
                                valueErrors.push(
                                    `${typeLabel.value} innehåller frasen $${fras} som saknas.
                                    Ta bort frasen $${fras} eller komplettera operationens data för att kunna skapa ${typeLabel.value.toLowerCase()}.`
                                );
                            }

                            errors.set(id, valueErrors);
                        }
                    });
                }
            }
        }

        return errors;
    }

    function onUpdate(data) {
        frasErrorsPerObject.value = checkFraser({ data, values: frasValues?.value });

        frasErrors.value = Array.from(frasErrorsPerObject.value.values()).flat();

        emit("update:content", {
            data,
            errors: frasErrorsPerObject.value,
        });
    }

    function insertFrasChar() {
        if (!editorComp.value?.editor?.state?.selection) {
            return;
        }

        editorComp.value?.editor.chain().focus().insertContent("$").run();
    }
</script>

<template>
    <div class="advanced-editor">
        <div v-if="frasErrors?.length > 0" class="errors">
            <div class="error" v-for="error in frasErrors" :key="error">
                <div class="img-container">
                    <img src="@/assets/alert_dark.svg" />
                </div>
                <p>{{ error }}</p>
            </div>
        </div>
        <div class="editor-container">
            <div v-if="enableBar" class="editor-bar">
                <div class="editor-buttons" v-if="!simpleEditor">
                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().toggleBold().run()"
                        :class="{ 'is-active': editorComp?.editor?.isActive('bold') }"
                    >
                        <img src="@/assets/editor/bold.svg" />
                    </button>

                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().toggleItalic().run()"
                        :class="{ 'is-active': editorComp?.editor?.isActive('italic') }"
                    >
                        <img src="@/assets/editor/italic.svg" />
                    </button>

                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().toggleStrike().run()"
                        :class="{ 'is-active': editorComp?.editor?.isActive('strike') }"
                    >
                        <img src="@/assets/editor/strike.svg" />
                    </button>
                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().toggleHeading({ level: 1 }).run()"
                        :class="{ 'is-active': editorComp?.editor?.isActive('heading', { level: 1 }) }"
                    >
                        <img src="@/assets/editor/heading_one.svg" />
                    </button>
                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().toggleHeading({ level: 2 }).run()"
                        :class="{ 'is-active': editorComp?.editor?.isActive('heading', { level: 2 }) }"
                    >
                        <img src="@/assets/editor/heading_two.svg" />
                    </button>
                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().toggleBulletList().run()"
                        :class="{ 'is-active': editorComp?.editor?.isActive('bulletList') }"
                    >
                        <img src="@/assets/editor/bullet_list.svg" />
                    </button>
                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().toggleOrderedList().run()"
                        :class="{ 'is-active': editorComp?.editor?.isActive('orderedList') }"
                    >
                        <img src="@/assets/editor/ordered_list.svg" />
                    </button>

                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().setHorizontalRule().run()"
                    >
                        <img src="@/assets/editor/horizontal_rule.svg" />
                    </button>
                </div>

                <div class="history-buttons">
                    <button v-if="withFraser" @click.prevent="insertFrasChar">
                        <img src="@/assets/editor/frasregister.svg" />
                    </button>

                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().undo().run()"
                        :disabled="!editorComp?.editor?.can().undo()"
                    >
                        <img src="@/assets/editor/undo.svg" />
                    </button>
                    <button
                        type="button"
                        @click.prevent="editorComp?.editor?.chain().focus().redo().run()"
                        :disabled="!editorComp?.editor?.can().redo()"
                    >
                        <img src="@/assets/editor/redo.svg" />
                    </button>
                </div>
            </div>
            <RichTextEditor
                ref="editorComp"
                :content="content"
                :fras-values="frasValues"
                :simple-editor="simpleEditor"
                @input="onUpdate"
            />
        </div>
        <slot
            name="customActions"
            :insertFrasChar="{
                can: () => !!editorComp?.value?.editor?.state?.selection,
                run: () => insertFrasChar(),
            }"
            :redo="{
                can: () => editorComp?.editor?.can().redo(),
                run: () => editorComp?.editor?.chain().focus().redo().run(),
            }"
            :undo="{
                can: () => editorComp?.editor?.can().undo(),
                run: () => editorComp?.editor?.chain().focus().undo().run(),
            }"
        />
    </div>
</template>

<style scoped lang="scss">
    @import "@/style/variables";
    @import "@/style/container";

    .advanced-editor {
        .errors {
            display: grid;
            background-color: $color-error-background;
            padding: 0.8rem;
            grid-gap: 0.5rem;
            margin-bottom: 1rem;
            border-radius: $border-radius-max;

            div {
                display: grid;
                grid-auto-flow: column;
                grid-gap: 0.5rem;

                .img-container {
                    display: grid;
                    height: $line-height-default;
                    place-items: center;
                }
            }
        }

        .editor-container {
            display: grid;
            grid-template-rows: max-content auto;
            width: $content-width;
            min-height: 30rem;
            background: $color-background-light;
            border: 1px solid $divider-color;
            border-radius: $border-radius-max;

            .editor-bar {
                $flex-gap: 1.5rem;

                background-color: $color-background-tool-menu;
                height: 3.5rem;
                border-top-left-radius: $border-radius-max;
                border-top-right-radius: $border-radius-max;
                padding: 1rem 1.5rem;
                display: flex;
                justify-content: space-between;
                gap: $flex-gap;

                .editor-buttons,
                .history-buttons {
                    display: flex;
                    gap: $flex-gap;

                    button {
                        background: transparent;
                        border: none;
                        display: grid;
                        justify-content: center;
                        align-content: center;

                        &:hover {
                            background-color: $color-hover;
                            border-radius: $border-radius;
                        }

                        &.is-active {
                            background-color: $color-hover;
                            border-radius: $border-radius;
                        }

                        &:disabled {
                            opacity: $disabled-opacity;
                        }
                    }
                }
            }
        }
    }
</style>
